// libs/sdk/src/lib/chain/chain-client.ts
import { ChainId as ChainId3 } from "@one-inch-community/models";
import { createPublicClient } from "viem";

// libs/sdk/src/lib/chain/transport-map.ts
import { fallback, http, webSocket } from "viem";
import { ChainId } from "@one-inch-community/models";
import { getEnvironmentValue } from "@one-inch-community/core/environment";

// libs/sdk/src/lib/chain/viem-custom-http-transport.ts
import { createTransport, RpcRequestError, UrlRequiredError } from "viem";

// node_modules/.pnpm/viem@2.21.28_typescript@5.5.3/node_modules/viem/utils/promise/createBatchScheduler.ts
var schedulerCache = /* @__PURE__ */ new Map();
function createBatchScheduler({
  fn,
  id,
  shouldSplitBatch,
  wait = 0,
  sort
}) {
  const exec = async () => {
    const scheduler = getScheduler();
    flush();
    const args = scheduler.map(({ args: args2 }) => args2);
    if (args.length === 0) return;
    fn(args).then((data) => {
      if (sort && Array.isArray(data)) data.sort(sort);
      for (let i = 0; i < scheduler.length; i++) {
        const { pendingPromise } = scheduler[i];
        pendingPromise.resolve?.([data[i], data]);
      }
    }).catch((err) => {
      for (let i = 0; i < scheduler.length; i++) {
        const { pendingPromise } = scheduler[i];
        pendingPromise.reject?.(err);
      }
    });
  };
  const flush = () => schedulerCache.delete(id);
  const getBatchedArgs = () => getScheduler().map(({ args }) => args);
  const getScheduler = () => schedulerCache.get(id) || [];
  const setScheduler = (item) => schedulerCache.set(id, [...getScheduler(), item]);
  return {
    flush,
    async schedule(args) {
      const pendingPromise = {};
      const promise = new Promise((resolve, reject) => {
        pendingPromise.resolve = resolve;
        pendingPromise.reject = reject;
      });
      const split = shouldSplitBatch?.([...getBatchedArgs(), args]);
      if (split) exec();
      const hasActiveScheduler = getScheduler().length > 0;
      if (hasActiveScheduler) {
        setScheduler({ args, pendingPromise });
        return promise;
      }
      setScheduler({ args, pendingPromise });
      setTimeout(exec, wait);
      return promise;
    }
  };
}

// libs/sdk/src/lib/chain/viem-custom-http-transport.ts
import { getHttpRpcClient } from "viem/utils";
function viemCustomHttpTransport(url, config = {}) {
  const {
    batch,
    fetchOptions,
    key = "http",
    name = "HTTP JSON-RPC",
    onFetchRequest,
    onFetchResponse,
    retryDelay
  } = config;
  return ({ chain, retryCount: retryCount_, timeout: timeout_ }) => {
    const { batchSize = 1e3, wait = 0 } = typeof batch === "object" ? batch : {};
    const retryCount = config.retryCount ?? retryCount_;
    const timeout = timeout_ ?? config.timeout ?? 1e4;
    const url_ = url || chain?.rpcUrls.default.http[0];
    if (!url_) throw new UrlRequiredError();
    const rpcClient = getHttpRpcClient(url_, {
      fetchOptions,
      onRequest: onFetchRequest,
      onResponse: onFetchResponse,
      timeout
    });
    return createTransport(
      {
        key,
        name,
        async request({ method, params }) {
          if (interceptMethods[method]) {
            return interceptMethods[method]();
          }
          const body = { method, params };
          const { schedule } = createBatchScheduler({
            id: url_,
            wait,
            shouldSplitBatch(requests) {
              return requests.length > batchSize;
            },
            fn: (body2) => rpcClient.request({
              body: body2
            }),
            sort: (a, b) => a.id - b.id
          });
          const fn = async (body2) => batch ? schedule(body2) : [
            await rpcClient.request({
              body: body2
            })
          ];
          const [{ error, result }] = await fn(body);
          if (error)
            throw new RpcRequestError({
              body,
              error,
              url: url_
            });
          return result;
        },
        retryCount,
        retryDelay,
        timeout,
        type: "http"
      },
      {
        fetchOptions,
        url: url_
      }
    );
  };
}
var interceptMethods = {
  "net_listening": () => true
};

// libs/sdk/src/lib/chain/transport-map.ts
var batchConfig = {
  wait: 300,
  batchSize: 50
};
function getTransport(chainId) {
  const httpTransports = transportMap[chainId];
  const wsTransports = transportWSMap[chainId];
  const transport = [
    ...httpTransports.map((rpc) => {
      if (typeof rpc === "string") {
        return http(rpc, {
          batch: batchConfig
        });
      }
      return http(rpc.rpc, {
        batch: { ...batchConfig, ...rpc.batchConfig }
      });
    }),
    ...wsTransports.map((rpc) => webSocket(rpc, {
      reconnect: {
        attempts: 5,
        delay: 5e3
      }
    }))
  ];
  const infuraRpc = getInfuraRpc(chainId);
  if (infuraRpc) {
    transport.push(viemCustomHttpTransport(infuraRpc, {
      batch: batchConfig,
      key: "infura"
    }));
  }
  return fallback(transport, {
    retryCount: 10,
    retryDelay: 3e4,
    rank: {
      interval: 6e4 * 5,
      sampleCount: httpTransports.length + wsTransports.length,
      timeout: 500,
      weights: {
        latency: 0.4,
        stability: 0.7
      }
    }
  });
}
function getInfuraRpc(chainId) {
  const apiKey = getEnvironmentValue("infuraApiKey");
  const host = getEnvironmentValue("oneInchDevPortalHost");
  const endpoint = infuraEndpoints[chainId];
  if (!apiKey || endpoint === "") return null;
  return `${host}/rpc/${chainId}`;
}
var infuraEndpoints = {
  [ChainId.eth]: "https://mainnet.infura.io/v3/",
  [ChainId.bnb]: "",
  [ChainId.matic]: "https://polygon-mainnet.infura.io/v3/",
  [ChainId.op]: "https://optimism-mainnet.infura.io/v3/",
  [ChainId.arbitrum]: "https://arbitrum-mainnet.infura.io/v3/",
  [ChainId.gnosis]: "",
  [ChainId.avalanche]: "https://avalanche-mainnet.infura.io/v3/",
  [ChainId.fantom]: "",
  [ChainId.aurora]: "",
  [ChainId.klaytn]: "",
  [ChainId.zkSyncEra]: ""
};
var transportMap = {
  [ChainId.eth]: [
    { rpc: "https://ethereum-rpc.publicnode.com", batchConfig: { batchSize: 200 } },
    { rpc: "https://ethereum.publicnode.com", batchConfig: { batchSize: 200 } },
    "https://rpc.mevblocker.io",
    "https://eth.merkle.io",
    "https://1rpc.io/eth",
    "https://eth-pokt.nodies.app",
    "https://eth.meowrpc.com",
    "https://eth.drpc.org",
    "https://eth.llamarpc.com",
    "https://eth-mainnet.public.blastapi.io",
    "https://rpc.ankr.com/eth",
    "https://cloudflare-eth.com",
    "https://endpoints.omniatech.io/v1/eth/mainnet/public"
  ],
  [ChainId.bnb]: [
    "https://bsc.publicnode.com",
    "https://bsc-rpc.publicnode.com",
    "https://endpoints.omniatech.io/v1/bsc/mainnet/public",
    "https://bsc-pokt.nodies.app",
    "https://bsc.meowrpc.com",
    "https://bsc.drpc.org"
  ],
  [ChainId.matic]: [
    "https://polygon-bor-rpc.publicnode.com",
    "https://polygon-rpc.com",
    "https://polygon-pokt.nodies.app",
    "https://polygon-heimdall-rpc.publicnode.com:443",
    // 'https://endpoints.omniatech.io/v1/matic/mainnet/public',
    "https://polygon-bor.publicnode.com",
    "https://polygon.drpc.org",
    "https://polygon.meowrpc.com",
    "https://polygon.llamarpc.com"
  ],
  [ChainId.op]: [
    "https://optimism-rpc.publicnode.com",
    "https://optimism.llamarpc.com",
    "https://optimism.publicnode.com",
    "https://op-pokt.nodies.app",
    "https://optimism.meowrpc.com",
    "https://optimism.drpc.org",
    "https://endpoints.omniatech.io/v1/op/mainnet/public"
  ],
  [ChainId.arbitrum]: [
    "https://arbitrum-one.publicnode.com",
    "https://arb-pokt.nodies.app",
    "https://arbitrum.meowrpc.com",
    "https://arbitrum.drpc.org"
  ],
  [ChainId.gnosis]: [
    "https://gnosis.publicnode.com",
    "https://gnosis-rpc.publicnode.com",
    "https://rpc.gnosischain.com",
    "https://gnosis.drpc.org"
  ],
  [ChainId.avalanche]: [
    "https://avalanche-c-chain.publicnode.com",
    "https://1rpc.io/avax/c",
    "https://endpoints.omniatech.io/v1/avax/mainnet/public",
    "https://avax.meowrpc.com",
    "https://avalanche.drpc.org"
  ],
  [ChainId.fantom]: [
    "https://fantom.publicnode.com",
    "https://fantom-pokt.nodies.app",
    "https://1rpc.io/ftm",
    "https://fantom.drpc.org"
  ],
  [ChainId.aurora]: [
    "https://mainnet.aurora.dev",
    "https://endpoints.omniatech.io/v1/aurora/mainnet/public",
    "https://1rpc.io/aurora",
    "https://aurora.drpc.org"
  ],
  [ChainId.klaytn]: [
    "https://klaytn.drpc.org",
    "https://rpc.ankr.com/klaytn",
    "https://klaytn.blockpi.network/v1/rpc/public"
  ],
  [ChainId.zkSyncEra]: [
    "https://mainnet.era.zksync.io",
    "https://zksync-era.blockpi.network/v1/rpc/public",
    "https://zksync.meowrpc.com",
    "https://zksync.drpc.org"
  ]
};
var transportWSMap = {
  [ChainId.eth]: [
    "wss://ethereum-rpc.publicnode.com",
    "wss://ethereum.publicnode.com",
    "wss://eth.drpc.org"
  ],
  [ChainId.bnb]: [
    "wss://bsc.publicnode.com",
    "wss://bsc-rpc.publicnode.com"
  ],
  [ChainId.matic]: [
    "wss://polygon.drpc.org",
    "wss://polygon-bor-rpc.publicnode.com",
    "wss://polygon-bor.publicnode.com",
    "wss://polygon-heimdall-rpc.publicnode.com:443/websocket"
  ],
  [ChainId.op]: [
    "wss://optimism.publicnode.com"
  ],
  [ChainId.arbitrum]: [
    "wss://arbitrum-one.publicnode.com"
  ],
  [ChainId.gnosis]: [
    "wss://rpc.gnosischain.com/wss",
    "wss://gnosis-rpc.publicnode.com",
    "wss://gnosis.publicnode.com"
  ],
  [ChainId.avalanche]: [
    "wss://avalanche-c-chain.publicnode.com"
  ],
  [ChainId.fantom]: [
    "wss://gnosis.publicnode.com"
  ],
  [ChainId.aurora]: [
    "wss://mainnet.aurora.dev"
  ],
  [ChainId.klaytn]: [
    "wss://public-en-cypress.klaytn.net/ws"
  ],
  [ChainId.zkSyncEra]: [
    "wss://mainnet.era.zksync.io/ws"
  ]
};

// libs/sdk/src/lib/chain/viem-chain-map.ts
import { ChainId as ChainId2 } from "@one-inch-community/models";
import {
  arbitrum,
  aurora,
  avalanche,
  bsc,
  fantom,
  gnosis,
  klaytn,
  mainnet,
  optimism,
  polygon,
  zkSync
} from "viem/chains";
var viemChainId = {
  [ChainId2.eth]: mainnet,
  [ChainId2.bnb]: bsc,
  [ChainId2.matic]: polygon,
  [ChainId2.op]: optimism,
  [ChainId2.arbitrum]: arbitrum,
  [ChainId2.gnosis]: gnosis,
  [ChainId2.avalanche]: avalanche,
  [ChainId2.fantom]: fantom,
  [ChainId2.aurora]: aurora,
  [ChainId2.klaytn]: klaytn,
  [ChainId2.zkSyncEra]: zkSync
};
function getChainById(chainId) {
  return viemChainId[chainId];
}

// libs/sdk/src/lib/chain/chain-client.ts
var viemClients = {
  [ChainId3.eth]: null,
  [ChainId3.bnb]: null,
  [ChainId3.matic]: null,
  [ChainId3.op]: null,
  [ChainId3.arbitrum]: null,
  [ChainId3.gnosis]: null,
  [ChainId3.avalanche]: null,
  [ChainId3.fantom]: null,
  [ChainId3.aurora]: null,
  [ChainId3.klaytn]: null,
  [ChainId3.zkSyncEra]: null
};
function buildViemDefaultClient(chainId) {
  const chain = getChainById(chainId);
  const transport = getTransport(chainId);
  return createPublicClient({
    batch: {
      multicall: batchConfig
    },
    chain,
    transport
  });
}
function getClient(chainId) {
  if (!viemClients[chainId]) {
    viemClients[chainId] = buildViemDefaultClient(chainId);
  }
  return viemClients[chainId];
}

// libs/sdk/src/lib/chain/layer2-chain.ts
import { ChainId as ChainId4 } from "@one-inch-community/models";
var layer2Chain = {
  [ChainId4.eth]: false,
  [ChainId4.bnb]: false,
  [ChainId4.matic]: false,
  [ChainId4.op]: true,
  [ChainId4.arbitrum]: true,
  [ChainId4.gnosis]: false,
  [ChainId4.avalanche]: false,
  [ChainId4.fantom]: false,
  [ChainId4.aurora]: false,
  [ChainId4.klaytn]: false,
  [ChainId4.zkSyncEra]: true
};
function isL2Chain(chainId) {
  return layer2Chain[chainId];
}

// libs/sdk/src/lib/chain/block-emitter-map.ts
import { ChainId as ChainId6 } from "@one-inch-community/models";
import {
  debounceTime,
  distinctUntilChanged,
  fromEvent,
  interval,
  map,
  merge,
  Observable,
  of,
  shareReplay,
  startWith,
  switchMap
} from "rxjs";

// libs/sdk/src/lib/chain/average-block-time.ts
import { ChainId as ChainId5 } from "@one-inch-community/models";
var averageBlockTime = {
  [ChainId5.eth]: 11e3,
  [ChainId5.bnb]: 3e3,
  [ChainId5.matic]: 3e3,
  [ChainId5.op]: 3e3,
  [ChainId5.arbitrum]: 3e3,
  [ChainId5.gnosis]: 5e3,
  [ChainId5.avalanche]: 3e3,
  [ChainId5.fantom]: 3e3,
  [ChainId5.aurora]: 3e3,
  [ChainId5.klaytn]: 3e3,
  [ChainId5.zkSyncEra]: 3e3
};

// libs/sdk/src/lib/chain/block-emitter-map.ts
var blockEmitterMap = {
  [ChainId6.eth]: null,
  [ChainId6.bnb]: null,
  [ChainId6.matic]: null,
  [ChainId6.op]: null,
  [ChainId6.arbitrum]: null,
  [ChainId6.gnosis]: null,
  [ChainId6.avalanche]: null,
  [ChainId6.fantom]: null,
  [ChainId6.aurora]: null,
  [ChainId6.klaytn]: null,
  [ChainId6.zkSyncEra]: null
};
var lastMouseMoveTimestamp = Date.now();
document.addEventListener("mousemove", () => lastMouseMoveTimestamp = Date.now());
function smartBlockListener(chainId) {
  let subs;
  let nowSlow = false;
  const isSlowLoad = () => {
    return Date.now() - lastMouseMoveTimestamp > 60 * 1e3;
  };
  return new Observable((subscriber) => {
    const handler = (block) => {
      subscriber.next(block);
      const isSlowLoadNow = isSlowLoad();
      if (isSlowLoadNow && !nowSlow) {
        nowSlow = true;
        subs?.unsubscribe();
        subs = blockSlowListener(chainId).subscribe(handler);
        console.log("slow block loader");
      } else if (!isSlowLoadNow && nowSlow) {
        nowSlow = false;
        subs?.unsubscribe();
        subs = blockListener(chainId).subscribe(handler);
        console.log("regular block loader");
      }
    };
    subs = blockListener(chainId).subscribe(handler);
    return () => {
      subs?.unsubscribe();
    };
  });
}
function blockListener(chainId) {
  return new Observable((subscriber) => {
    const client = getClient(chainId);
    return client.watchBlocks({ onBlock: (block) => subscriber.next(block) });
  }).pipe(
    shareReplay({ bufferSize: 1, refCount: true })
  );
}
function blockSlowListener(chainId) {
  const client = getClient(chainId);
  return interval(30 * 1e3).pipe(
    switchMap(() => client.getBlock())
  );
}
function isWindowVisibleAndFocused() {
  const isFocused = document.hasFocus();
  const isVisible = document.visibilityState === "visible";
  return isFocused && isVisible;
}
function isWindowVisibleAndFocused$() {
  let isFirst = true;
  return merge(
    fromEvent(window, "focus"),
    fromEvent(window, "blur"),
    fromEvent(document, "visibilitychange")
  ).pipe(
    startWith(null),
    map(() => {
      if (isFirst) {
        isFirst = false;
        return true;
      }
      return isWindowVisibleAndFocused();
    }),
    distinctUntilChanged()
  );
}
function getBlockEmitter(chainId) {
  if (blockEmitterMap[chainId] === null) {
    blockEmitterMap[chainId] = isWindowVisibleAndFocused$().pipe(
      switchMap((state) => {
        if (state) return smartBlockListener(chainId);
        return of();
      }),
      debounceTime(averageBlockTime[chainId] / 2),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }
  return blockEmitterMap[chainId];
}

// libs/sdk/src/lib/chain/get-balance.ts
import { parseAbi } from "viem";

// libs/sdk/src/lib/chain/is-native-token.ts
import { isAddressEqual } from "viem";
var nativeTokenAddress = "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee";
function isNativeToken(address) {
  return isAddressEqual(nativeTokenAddress, address);
}

// libs/sdk/src/lib/chain/block-time-cache.ts
import { TimeCache } from "@one-inch-community/core/cache";
var BlockTimeCache = class {
  constructor() {
    this.timeCacheStorage = {};
  }
  set(chainId, key, value) {
    let cache3 = this.timeCacheStorage[chainId];
    if (!cache3) {
      cache3 = new TimeCache(averageBlockTime[chainId]);
      this.timeCacheStorage[chainId] = cache3;
    }
    cache3.set(key, value);
  }
  get(chainId, key) {
    const cache3 = this.timeCacheStorage[chainId];
    if (!cache3) {
      return null;
    }
    return cache3.get(key);
  }
};

// libs/sdk/src/lib/chain/get-balance.ts
var abi = parseAbi([
  "function balanceOf(address _owner) public view returns (uint256 balance)"
]);
var cache = new BlockTimeCache();
async function getBalance(chainId, wallet, contract) {
  const id = [wallet, contract].join(":");
  const cachedValue = cache.get(chainId, id);
  if (cachedValue !== null) {
    return cachedValue;
  }
  const client = getClient(chainId);
  if (isNativeToken(contract)) {
    return await client.getBalance({ address: wallet });
  }
  const result = await client.readContract({
    abi,
    functionName: "balanceOf",
    args: [wallet],
    address: contract
  });
  cache.set(chainId, id, result);
  return result;
}
async function getBalances(chainId, walletAddress, addresses) {
  const result = {};
  await Promise.all(addresses.map((address) => getBalance(chainId, walletAddress, address).then((balance) => result[address] = balance.toString())));
  return result;
}

// libs/sdk/src/lib/chain/get-allowance.ts
import { parseAbi as parseAbi2, maxUint256 } from "viem";
import { switchMap as switchMap2 } from "rxjs";
var abi2 = parseAbi2([
  "function allowance(address owner, address spender) public view returns (uint256)"
]);
var cache2 = new BlockTimeCache();
async function getAllowance(chainId, contract, owner, spender) {
  const id = [chainId, contract, owner, spender].join(":");
  const cachedValue = cache2.get(chainId, id);
  if (cachedValue !== null) {
    return cachedValue;
  }
  const client = getClient(chainId);
  if (isNativeToken(contract)) {
    return maxUint256;
  }
  const result = await client.readContract({
    abi: abi2,
    functionName: "allowance",
    args: [owner, spender],
    address: contract
  });
  cache2.set(chainId, id, result);
  return result;
}
function listenAllowance(chainId, contract, owner, spender) {
  return getBlockEmitter(chainId).pipe(
    switchMap2(() => getAllowance(chainId, contract, owner, spender))
  );
}

// libs/sdk/src/lib/chain/eip-2612.ts
import { parseAbi as parseAbi3, toFunctionSelector, maxUint256 as maxUint2562 } from "viem";
import { QueueCache } from "@one-inch-community/core/cache";
var eipPermitAbi = "function permit(address owner, address spender, uint value, uint deadline, uint8 v, bytes32 r, bytes32 s) external";
var daiPermitAbi = "function permit(address holder, address spender, uint256 nonce, uint256 expiry, bool allowed, uint8 v, bytes32 r, bytes32 s) external";
var apiPermit = () => parseAbi3([
  eipPermitAbi,
  daiPermitAbi
]);
var abiNonce = () => parseAbi3([
  "function nonces(address owner) external view returns (uint)",
  "function nonce(address owner) external view returns (uint)",
  "function getNonce(address owner) external view returns (uint)",
  "function _nonces(address owner) external view returns (uint)"
]);
var abiDomainSeparator = parseAbi3([
  "function DOMAIN_SEPARATOR() external view returns (bytes32)"
]);
var versionAbi = parseAbi3([
  "function version() view returns (string)"
]);
var nameAbi = parseAbi3([
  "function name() view returns (string)"
]);
var permitSelector = () => toFunctionSelector(apiPermit()[0]);
var permitSelectorDai = () => toFunctionSelector(apiPermit()[0]);
var domainSeparatorSelector = () => toFunctionSelector(abiDomainSeparator[0]);
var nonceSelectors = () => abiNonce().map((abi6) => toFunctionSelector(abi6));
async function circularViewCall(chainId, contract, abiList, args) {
  const client = getClient(chainId);
  for (const abi6 of abiList) {
    try {
      return await client.readContract({
        args,
        abi: [abi6],
        functionName: abi6.name,
        address: contract
      });
    } catch (error) {
    }
  }
  throw new Error("fail circular view call");
}
function getNonce(chainId, contract, owner) {
  return circularViewCall(chainId, contract, abiNonce(), [owner]);
}
function getName(chainId, contract) {
  return circularViewCall(chainId, contract, nameAbi, []);
}
function getVersion(chainId, contract) {
  return circularViewCall(chainId, contract, versionAbi, []);
}
function getDomainSeparator(chainId, contract) {
  return circularViewCall(chainId, contract, abiDomainSeparator, []);
}
var codeCache = new QueueCache(5);
async function getCode(chainId, contract) {
  const id = [chainId, contract].join(":");
  const cacheValue = codeCache.get(id);
  if (cacheValue) {
    return cacheValue;
  }
  const client = getClient(chainId);
  const code = await client.getBytecode({
    address: contract
  });
  if (!code) {
    throw new Error(`Contract not found: ${contract}`);
  }
  codeCache.set(id, code);
  return code;
}
function checkExistDomainSeparatorSelector(code) {
  return code.includes(domainSeparatorSelector().replace("0x", "").toLowerCase());
}
function checkExistNonceSelector(code) {
  return nonceSelectors().some((selector) => code.includes(selector.replace("0x", "").toLowerCase()));
}
function checkExistPermitSelector(code) {
  const _permitSelector = permitSelector().replace("0x", "").toLowerCase();
  return code.includes(_permitSelector) || isDaiLikePermit(code);
}
function isDaiLikePermit(code) {
  const _permitSelectorDai = permitSelectorDai().replace("0x", "").toLowerCase();
  return code.includes(_permitSelectorDai);
}
async function isSupportedEIP2612(chainId, contract) {
  try {
    const code = await getCode(chainId, contract);
    const lowerCaseCode = code.toLowerCase();
    let isSupportedEIP26122 = true;
    isSupportedEIP26122 && (isSupportedEIP26122 = checkExistPermitSelector(lowerCaseCode));
    isSupportedEIP26122 && (isSupportedEIP26122 = checkExistDomainSeparatorSelector(lowerCaseCode));
    isSupportedEIP26122 && (isSupportedEIP26122 = checkExistNonceSelector(lowerCaseCode));
    return isSupportedEIP26122;
  } catch (error) {
    return false;
  }
}
async function makePermitTypeData(chainId, contract, owner, spender) {
  const [
    code,
    name,
    version,
    domainSeparator,
    nonce
  ] = await Promise.all([
    getCode(chainId, contract),
    getName(chainId, contract),
    getVersion(chainId, contract),
    getDomainSeparator(chainId, contract),
    getNonce(chainId, contract, owner)
  ]);
  const isDaiLike = isDaiLikePermit(code);
  const [EIP712DomainType, EIP712DomainData] = getEIP712DomainDataAndType(
    chainId,
    contract,
    name,
    version
  );
  const [PermitType, PermitData] = getPermitDataAndType(
    isDaiLike,
    owner,
    spender,
    nonce
  );
  return {
    primaryType: "Permit",
    types: {
      EIP712Domain: EIP712DomainType,
      Permit: PermitType
    },
    domain: EIP712DomainData,
    message: PermitData
  };
}
function getPermitDataAndType(isDaiLike, owner, spender, nonce) {
  const type = isDaiLike ? daiPermitModelFields : eip2612PermitModelFields;
  const data = {};
  if (isDaiLike) {
    data["holder"] = owner;
    data["spender"] = spender;
    data["nonce"] = nonce;
    data["expiry"] = Math.round(Date.now() / 1e3) + 300;
    data["allowed"] = true;
  } else {
    data["owner"] = owner;
    data["spender"] = spender;
    data["value"] = maxUint2562;
    data["nonce"] = nonce;
    data["deadline"] = Math.round(Date.now() / 1e3) + 300;
  }
  return [type, data];
}
function getEIP712DomainDataAndType(chainId, contract, name, version) {
  const type = [
    { name: "name", type: "string" },
    { name: "version", type: "string" },
    { name: "chainId", type: "uint256" },
    { name: "verifyingContract", type: "address" }
  ];
  const data = {
    name,
    version,
    chainId,
    verifyingContract: contract
  };
  return [type, data];
}
var eip2612PermitModelFields = [
  { name: "owner", type: "address" },
  { name: "spender", type: "address" },
  { name: "value", type: "uint256" },
  { name: "nonce", type: "uint256" },
  { name: "deadline", type: "uint256" }
];
var daiPermitModelFields = [
  { name: "holder", type: "address" },
  { name: "spender", type: "address" },
  { name: "nonce", type: "uint256" },
  { name: "expiry", type: "uint256" },
  { name: "allowed", type: "bool" }
];

// libs/sdk/src/lib/chain/permit2.ts
import {
  maxUint160,
  parseSignature,
  encodeFunctionData,
  encodeAbiParameters,
  decodeAbiParameters,
  maxUint48,
  toBytes,
  toHex
} from "viem";
import { ChainId as ChainId9 } from "@one-inch-community/models";

// libs/sdk/src/lib/chain/contracts.ts
import { ChainId as ChainId8 } from "@one-inch-community/models";

// libs/sdk/src/lib/chain/is-chain-id.ts
import { ChainId as ChainId7 } from "@one-inch-community/models";
function isChainId(chainId) {
  return ChainId7[chainId] !== void 0;
}
function requireChainId(chainId) {
  if (!isChainId(chainId)) throw new Error(`chain ${chainId} not supported`);
}

// libs/sdk/src/lib/chain/contracts.ts
function getOneInchRouterV6ContractAddress(chainId) {
  requireChainId(chainId);
  if (chainId === ChainId8.zkSyncEra) {
    return "0x6fd4383cb451173d5f9304f041c7bcbf27d561ff";
  }
  return "0x111111125421ca6dc452d289314280a0f8842a65";
}
function permit2ContractAddress(chainId) {
  requireChainId(chainId);
  if (chainId === ChainId8.zkSyncEra) {
    return "0x0000000000225e31D15943971F47aD3022F714Fa";
  }
  return "0x000000000022D473030F116dDEE9F6B43aC78BA3";
}
function oneInchSpotPriceOracle(chainId) {
  requireChainId(chainId);
  if (chainId === ChainId8.zkSyncEra) {
    return "0xc9bB6e4FF7dEEa48e045CEd9C0ce016c7CFbD500";
  }
  return "0x0AdDd25a91563696D8567Df78D5A01C9a991F9B8";
}

// libs/sdk/src/lib/chain/permit2.ts
import { LongTimeAsyncCache } from "@one-inch-community/core/cache";
var permitLongTimeCache = new LongTimeAsyncCache("permit2", 29);
var permit2SupportedChain = {
  [ChainId9.eth]: false,
  // true,
  [ChainId9.bnb]: false,
  // true,
  [ChainId9.matic]: false,
  // true,
  [ChainId9.op]: false,
  // true,
  [ChainId9.arbitrum]: false,
  // true,
  [ChainId9.gnosis]: false,
  // true,
  [ChainId9.avalanche]: false,
  // true,
  [ChainId9.fantom]: false,
  [ChainId9.aurora]: false,
  [ChainId9.klaytn]: false,
  [ChainId9.zkSyncEra]: true
};
var loadAbi = () => import("./permit2_abi-TSO4Q6OK.js").then((m) => m.default);
function isSupportPermit2(chainId) {
  return permit2SupportedChain[chainId] ?? false;
}
async function getPermit2TypeData(chainId, tokenAddress, walletAddress, spenderAddress) {
  if (!isSupportPermit2(chainId)) {
    throw new Error(`chain ${chainId} not supported Permit2`);
  }
  const { nonce } = await getAllowanceData(chainId, tokenAddress, walletAddress, spenderAddress);
  const permitSingle = {
    details: {
      token: tokenAddress,
      amount: maxUint160,
      expiration: Number(maxUint48),
      // 30 day
      nonce
    },
    spender: spenderAddress,
    sigDeadline: Number(maxUint48)
    // 30 day
  };
  const domain = {
    chainId,
    name: "Permit2",
    verifyingContract: permit2ContractAddress(chainId)
  };
  return {
    domain,
    account: walletAddress,
    primaryType: "PermitSingle",
    types: PERMIT_TYPES,
    message: permitSingle
  };
}
async function getAllowanceData(chainId, tokenAddress, walletAddress, spenderAddress) {
  if (!isSupportPermit2(chainId)) {
    throw new Error(`chain ${chainId} not supported Permit2`);
  }
  const client = getClient(chainId);
  const result = await client.readContract({
    address: permit2ContractAddress(chainId),
    abi: await loadAbi(),
    functionName: "allowance",
    args: [tokenAddress, walletAddress, spenderAddress]
  });
  const [amount, expiration, nonce] = result;
  return { amount, expiration, nonce };
}
async function savePermit(chainId, tokenAddress, walletAddress, spenderAddress, signData, permitSingle) {
  const id = [chainId, tokenAddress, walletAddress, spenderAddress].join(":");
  await permitLongTimeCache.set(id, {
    permitSingle,
    signature: signData
  });
}
async function getPermit(chainId, tokenAddress, walletAddress, spenderAddress) {
  const id = [chainId, tokenAddress, walletAddress, spenderAddress].join(":");
  return await permitLongTimeCache.get(id);
}
async function hasPermit(chainId, tokenAddress, walletAddress, spenderAddress) {
  const result = await getPermit(chainId, tokenAddress, walletAddress, spenderAddress);
  return result !== null;
}
async function preparePermit2ForSwap(chainId, owner, permit2Sign, permitSingle) {
  const signature = parseSignature(permit2Sign);
  const permitCallFull = encodeFunctionData({
    abi: await loadAbi(),
    functionName: "permit",
    args: [
      owner,
      permitSingle,
      `${signature.r}${trim0x(computeYParityAndS(signature.s, signature.v === 27n ? 0 : 1))}`
    ]
  });
  const permitCallParams = cutSelector(permitCallFull);
  const token = permitSingle.details.token;
  const spender = permitSingle.spender;
  return decompressPermit(compressPermit(permitCallParams), token, owner, spender);
}
function computeYParityAndS(s, yParity) {
  const sHex = toBytes(s);
  if (yParity === 1) {
    sHex[0] |= 128;
  }
  return toHex(sHex);
}
function cutSelector(data) {
  const hexPrefix = "0x";
  return hexPrefix + data.substring(hexPrefix.length + 8);
}
function trim0x(bigNumber) {
  const s = bigNumber.toString();
  if (s.startsWith("0x")) {
    return s.substring(2);
  }
  return s;
}
function compressPermit(permit) {
  if (permit.length !== 706) {
    throw new Error("");
  }
  const [
    owner,
    token,
    amount,
    expiration,
    nonce,
    spender,
    sigDeadline,
    signature
  ] = decodeAbiParameters([
    { name: "owner", type: "address" },
    { name: "token", type: "address" },
    { name: "amount", type: "uint160" },
    { name: "expiration", type: "uint48" },
    { name: "nonce", type: "uint48" },
    { name: "spender", type: "address" },
    { name: "sigDeadline", type: "uint256" },
    { name: "signature", type: "bytes" }
  ], permit);
  const compressResult = [
    amount.toString(16).padStart(40, "0"),
    expiration.toString() === maxUint48.toString() ? "00000000" : (BigInt(expiration) + 1n).toString(16).padStart(8, "0"),
    nonce.toString(16).padStart(8, "0"),
    sigDeadline.toString() === maxUint48.toString() ? "00000000" : (sigDeadline + 1n).toString(16).padStart(8, "0"),
    BigInt(signature).toString(16).padStart(128, "0")
  ].join("");
  return `0x${compressResult}`;
}
function decompressPermit(permit, token, owner, spender) {
  if (permit.length !== 194) {
    throw new Error("");
  }
  const args = {
    amount: BigInt(permit.slice(0, 42)),
    expiration: Number("0x" + permit.slice(42, 50)),
    nonce: Number("0x" + permit.slice(50, 58)),
    sigDeadline: BigInt("0x" + permit.slice(58, 66)),
    r: "0x" + permit.slice(66, 130),
    vs: "0x" + permit.slice(130, 194)
  };
  return encodeAbiParameters(
    [
      { name: "owner", type: "address" },
      { name: "token", type: "address" },
      { name: "amount", type: "uint160" },
      { name: "expiration", type: "uint48" },
      { name: "nonce", type: "uint48" },
      { name: "spender", type: "address" },
      { name: "sigDeadline", type: "uint256" },
      { name: "signature", type: "bytes" }
    ],
    [
      owner,
      token,
      args.amount,
      args.expiration === 0 ? Number(maxUint48) : args.expiration - 1,
      args.nonce,
      spender,
      args.sigDeadline === 0n ? maxUint48 : args.sigDeadline - 1n,
      args.r + trim0x(args.vs)
    ]
  );
}
var PERMIT_DETAILS = [
  { name: "token", type: "address" },
  { name: "amount", type: "uint160" },
  { name: "expiration", type: "uint48" },
  { name: "nonce", type: "uint48" }
];
var EIP712_DOMAIN = [
  { name: "name", type: "string" },
  { name: "chainId", type: "uint256" },
  { name: "verifyingContract", type: "address" }
];
var PERMIT_TYPES = {
  PermitSingle: [
    { name: "details", type: "PermitDetails" },
    { name: "spender", type: "address" },
    { name: "sigDeadline", type: "uint256" }
  ],
  PermitDetails: PERMIT_DETAILS,
  EIP712Domain: EIP712_DOMAIN
};

// libs/sdk/src/lib/chain/wrapper-native-token-map.ts
import { ChainId as ChainId10 } from "@one-inch-community/models";
import { parseAbi as parseAbi4 } from "viem";
var wrapperNativeTokenMap = {
  [ChainId10.eth]: "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2",
  [ChainId10.bnb]: "0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c",
  [ChainId10.matic]: "0x0d500b1d8e8ef31e21c99d1db9a6444d3adf1270",
  [ChainId10.op]: "0x4200000000000000000000000000000000000006",
  [ChainId10.arbitrum]: "0x82af49447d8a07e3bd95bd0d56f35241523fbab1",
  [ChainId10.gnosis]: "0xe91d153e0b41518a2ce8dd3d7944fa863463a97d",
  [ChainId10.avalanche]: "0xb31f66aa3c1e785363f0875a1b74e27b85fd66c7",
  [ChainId10.fantom]: "0x21be370d5312f44cb42ce377bc9b8a0cef1a4c83",
  [ChainId10.aurora]: "0xc9bdeed33cd01541e1eed10f90519d2c06fe3feb",
  [ChainId10.klaytn]: "0xe4f05a66ec68b54a58b17c22107b02e0232cc817",
  [ChainId10.zkSyncEra]: "0x5aea5775959fbc2557cc8789bc1bf90a239d9a91"
};
var abi3 = parseAbi4([
  "function deposit() public payable"
]);
function getWrapperNativeTokenAddress(chainId) {
  return wrapperNativeTokenMap[chainId];
}
function getWrapperNativeToken(chainId) {
  const chain = getChainById(chainId);
  return {
    chainId,
    symbol: `W${chain.nativeCurrency.symbol}`,
    name: `W${chain.nativeCurrency.symbol}`,
    decimals: chain.nativeCurrency.decimals,
    address: wrapperNativeTokenMap[chainId],
    isInternalWrapToken: true
  };
}
function getSymbolFromWrapToken(token) {
  if (token.isInternalWrapToken) {
    return token.symbol.slice(1);
  }
  return token.symbol;
}
async function estimateWrap(chainId, value) {
  const client = getClient(chainId);
  const address = getWrapperNativeTokenAddress(chainId);
  return await client.estimateContractGas({
    abi: abi3,
    address,
    value,
    functionName: "deposit"
  });
}
async function getWrapSimulationRequest(chainId, value) {
  const client = getClient(chainId);
  const address = getWrapperNativeTokenAddress(chainId);
  const result = await client.simulateContract({
    abi: abi3,
    address,
    value,
    functionName: "deposit"
  });
  return result.request;
}

// libs/sdk/src/lib/chain/wait-transaction.ts
function waitTransaction(chain, hash, blockTag = "latest") {
  const client = getClient(chain);
  return new Promise((resolve) => {
    const handler = async () => {
      try {
        const tx = await client.getTransaction({ hash, blockTag });
        if (tx.blockNumber) {
          unwatch();
          resolve();
        }
      } catch {
      }
    };
    const unwatch = client.watchBlocks(
      { onBlock: handler }
    );
    handler().catch();
  });
}
async function waitTransactions(chain, hashes) {
  return await Promise.all(hashes.map((hash) => waitTransaction(chain, hash)));
}

// libs/sdk/src/lib/chain/get-approve.ts
import { parseAbi as parseAbi5 } from "viem";
var abi4 = parseAbi5([
  "function approve(address _spender, uint256 _value) public returns (bool success)"
]);
async function getApproveSimulation(chainId, contract, owner, spender, value) {
  const client = getClient(chainId);
  if (isNativeToken(contract)) {
    throw new Error("");
  }
  return await client.simulateContract({
    abi: abi4,
    account: owner,
    functionName: "approve",
    args: [spender, value],
    address: contract
  });
}

// libs/sdk/src/lib/chain/cancel-fusion-order.ts
import { parseAbi as parseAbi6 } from "viem";
var abi5 = parseAbi6([
  "function cancelOrder(uint256 makerTraits, bytes32 orderHash) public"
]);
async function cancelFusionOrder(chainId, owner, makerTraits, orderHash) {
  const client = getClient(chainId);
  const contract = getOneInchRouterV6ContractAddress(chainId);
  return await client.simulateContract({
    abi: abi5,
    account: owner,
    functionName: "cancelOrder",
    args: [BigInt(makerTraits), orderHash],
    address: contract
  });
}
export {
  BlockTimeCache,
  averageBlockTime,
  cancelFusionOrder,
  estimateWrap,
  getAllowance,
  getAllowanceData,
  getApproveSimulation,
  getBalance,
  getBalances,
  getBlockEmitter,
  getChainById,
  getClient,
  getOneInchRouterV6ContractAddress,
  getPermit,
  getPermit2TypeData,
  getSymbolFromWrapToken,
  getWrapSimulationRequest,
  getWrapperNativeToken,
  getWrapperNativeTokenAddress,
  hasPermit,
  isChainId,
  isL2Chain,
  isNativeToken,
  isSupportPermit2,
  isSupportedEIP2612,
  layer2Chain,
  listenAllowance,
  makePermitTypeData,
  nativeTokenAddress,
  oneInchSpotPriceOracle,
  permit2ContractAddress,
  preparePermit2ForSwap,
  requireChainId,
  savePermit,
  waitTransaction,
  waitTransactions,
  wrapperNativeTokenMap
};
//# sourceMappingURL=index.esm.js.map
